/* @flow */
/* eslint-env es6,browser */

import * as u from '../utils';
import * as fetch from '../utils/fetch';
import bidbase from './bidbase';
import bm from '../bidmanager';

/**
 * @module bidders/adkernel
 */
/**
 * @global
 * @typedef {Object} adkernelConfig
 * @description extends {@link hbConfig}
 * @property {string} host
 */
/**
 * @description Implements functionality for adkernel header bidding
 * @extends _bidbase
 * @requires {@link module:utils}
 */
class _adkernelbid extends bidbase {
  _config: adkernelConfig;
  _DefConfig: adkernelConfig;
  _bidRequest: Object;
  constructor() {
    super('adkernel');
    this._bidRequest = {
      impid: {}
    };
    this._DefConfig = u.extend(true, {}, this._DefConfig, {
      host: ''
    });
    this._config = u.extend(true, {}, this._DefConfig);
    this._mandatoryConfigs = ['host'];
    this._posId = 'zoneid';
    this._isSupported = u.isJSONSupported() && fetch.isSupported();
  }

  /**
   * Process RTB bid response. Response Format:
   * _bidResponses[impid].seatbid[0].bid[0].price - ecpm
   * _bidResponses[impid].seatbid[0].bid[0].adm - ad creative
   * _bidResponses[impid].seatbid[0].bid[0].nurl - impression recording pixel
   * @param {Array<string>} posList - array of positions
   * @param {Object.<string, hbPosData>} ret
   * @return {Object.<string, hbPosData>}
   * @access private
   */
  _processBidResponse(posList: Array<string>, ret: hbPartnerData): hbPartnerData {
    posList.forEach(pos => {
      let impid = this._bidRequest.impid[pos];
      let resp = this._bidResponses[impid];
      if (!this._isValidRTBResponse(resp) || impid !== resp.seatbid[0].bid[0].impid) {
        return;
      }
      let res = resp.seatbid[0].bid[0];
      //adm - creative, nurl - impression pixel
      if (res && res.price && res.nurl && res.adm) {
        let nurl = res.nurl.replace(/\$\{AUCTION_PRICE\}/i, res.price);
        this._handleBidPosData(ret, pos, res.price,
                               impid, decodeURIComponent(res.adm), nurl);
      }
    });
    return ret;
  }

  /**
   * Build request params in RTB format
   * @access private
   */
  _buildReqParams(pos: string): Object {
    //generate unique request id
    this._bidRequest.impid[pos] = u.getRandom();

    // build params for bid request
    let isSecure = ((location.protocol === 'https') ? 1 : 0);
    let params = {
      id: u.getRandom(),
      site: {
        domain: location.host
      },
      device: {
        ua: 'caller',
        ip: 'caller'
      },
      imp: [{
        id: this._bidRequest.impid[pos],
        banner: {
          w: this._config.posTokenMap[pos].width,
          h: this._config.posTokenMap[pos].height
        },
        secure: isSecure
      }]
    };

    return {
      zone: this._config.posTokenMap[pos].zoneid,
      r: JSON.stringify(params)
    };
  }

  _getBid(pos: string): Promise<*> {
    let url = `//${this._config.host}/rtbg?ad_type=rtb&`;
    let opt = {
      data: this._buildReqParams(pos),
      timeout: this._config.timeout,
      logPrefix: pos
    };
    return fetch.JSON(url, opt)
      .then(json => this._bidResponses[this._bidRequest.impid[pos]] = json);
  }

  /**
   * get bids for given positions
   * @param {Array<string>} posList
   * @param {Object.<string, hbPosData>} ret
   * @return {Promise<Object.<string, hbPosData>>}
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    this._bidResponses = {};

    //Create promise array with all bid requests and resolve
    let pArray = posList.map(pos => this._getBid(pos));
    return u.allSettledPromise(pArray)
      .then(arr => {
        let fValues = arr.filter(v => v.state !== 'fulfilled').map(v => v.reason);
        if (fValues.length) { // Log all failed promise errors
          u.logError(`${this._name} bidding errors: ${fValues.join(',')}`);
        }
        return this._processBidResponse(posList, ret);
      }, error => {
        u.logError(`${this._name} bidding failed: ${error || ''}`);
        return ret;
      });
  }
}

//add bidder to bidmanager
let adkernelbid = new _adkernelbid();
bm.addBidder('adkernel', adkernelbid);
export default adkernelbid;
